import React, { useEffect } from 'react';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { ClerkProvider, useAuth } from '@clerk/clerk-react';
import { ConvexProviderWithClerk } from "convex/react-clerk";

// Import the generated route tree
import { routeTree } from './routeTree.gen';

// Context providers
import { ToastProvider } from './contexts/ToastContext';

// Convex client and feature flags
import { convex } from './lib/convex';
import { featureFlags } from './services/featureFlags';

// Get Clerk publishable key
const clerkPubKey = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

if (!clerkPubKey) {
  throw new Error('Missing Clerk Publishable Key');
}

// Create a router instance
const router = createRouter({ routeTree });

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

// Inner app component that uses Clerk auth for Convex
const AppWithConvex: React.FC = () => {
  const { isLoaded } = useAuth();

  // Initialize feature flags when auth is loaded
  useEffect(() => {
    if (isLoaded) {
      featureFlags.initialize();
    }
  }, [isLoaded]);

  // Always wrap with ConvexProvider if CONVEX_URL is configured
  // This ensures components using Convex hooks don't throw errors
  // Feature flags control data fetching, not provider availability
  if (process.env.REACT_APP_CONVEX_URL) {
    return (
      <ConvexProviderWithClerk client={convex} useAuth={useAuth}>
        <ToastProvider>
          <RouterProvider router={router} />
        </ToastProvider>
      </ConvexProviderWithClerk>
    );
  }

  // Fallback when Convex is not configured at all
  return (
    <ToastProvider>
      <RouterProvider router={router} />
    </ToastProvider>
  );
};

const App: React.FC = () => {
  return (
    <ClerkProvider
      publishableKey={clerkPubKey}
      signInFallbackRedirectUrl="/"
      signUpFallbackRedirectUrl="/"
      signInForceRedirectUrl="/"
      signUpForceRedirectUrl="/"
    >
      <AppWithConvex />
    </ClerkProvider>
  );
};

export default App;